import React, { useCallback, useMemo } from 'react';
import LayoutStrings from 'screens/Applications&WorkOrders/StepsLayout/Constants/layoutStrings';
import { useFormikContext } from 'formik';
import { Button } from '@agconnections/grow-ui';

import {
  FormValues,
  PersonCompany
} from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import FormGrid, { FormRowConfig } from 'components/FormGrid';
import { COL_SPAN_ONE_THIRD } from 'components/FormGrid/constants';
import TrashIcon from 'components/Icons/TrashIcon';
import SelectApplicator from '../SelectApplicator';
import filterAvailableApplicators from '../../helpers/filterAvailableApplicators';

export interface ApplicatorRowProps {
  applicator: {
    personId?: string;
    companyId?: string;
  };
  index: number;
  onRemove: () => void;
  onPersonChange: (value: string) => void;
  onCompanyChange: (value: string) => void;
  people: { id: string; name: string; licenseNumber?: string }[];
  companies: { id: string; name: string }[];
  peopleLoading: boolean;
  companiesLoading: boolean;
  loadPeople: () => void;
  loadCompanies: () => void;
}

const ApplicatorRow: React.FC<ApplicatorRowProps> = ({
  applicator,
  index,
  onRemove,
  onPersonChange,
  onCompanyChange,
  people,
  companies,
  peopleLoading,
  companiesLoading,
  loadPeople,
  loadCompanies
}) => {
  const { values } = useFormikContext<FormValues>();

  // Format people data for SelectApplicator
  const formattedPeople = useMemo(() => {
    return filterAvailableApplicators(
      people,
      values.applicators,
      applicator.personId
    ).map(person => ({
      ...person,
      id: person.id,
      name: person.name,
      type: 'People'
    }));
  }, [people, values.applicators, applicator.personId]);

  // Format companies data for SelectApplicator
  const formattedCompanies = useMemo(() => {
    return companies.map(company => ({
      id: company.id,
      name: company.name,
      type: 'Company'
    }));
  }, [companies]);

  const handlePersonSelection = useCallback(
    (value: PersonCompany | null) => {
      if (value?.type === 'People') {
        onPersonChange(value.id);
      } else if (value === null) {
        onPersonChange('');
      }
    },
    [onPersonChange]
  );

  const handleCompanySelection = useCallback(
    (value: PersonCompany | null) => {
      if (value?.type === 'Company') {
        onCompanyChange(value.id);
      } else if (value === null) {
        onCompanyChange('');
      }
    },
    [onCompanyChange]
  );

  // Get selected person value
  const getSelectedPersonValue = useCallback(() => {
    if (!applicator.personId) return null;

    const selectedPerson = people.find(
      person => person.id === applicator.personId
    );
    return selectedPerson
      ? {
          id: selectedPerson.id,
          name: selectedPerson.name,
          type: 'People',
          licenseNumber: selectedPerson.licenseNumber || ''
        }
      : null;
  }, [applicator.personId, people]);

  // Get selected company value
  const getSelectedCompanyValue = React.useCallback(() => {
    if (!applicator.companyId) return null;

    const selectedCompany = companies.find(
      company => company.id === applicator.companyId
    );
    return selectedCompany
      ? {
          id: selectedCompany.id,
          name: selectedCompany.name,
          type: 'Company'
        }
      : null;
  }, [applicator.companyId, companies]);

  const formRows: FormRowConfig[] = [
    {
      id: `applicator-row-${index}`,
      fields: [
        {
          id: `applicator-person-${index}`,
          label: `${LayoutStrings.fields.applicator} ${index + 1}`,
          colSpan: COL_SPAN_ONE_THIRD,
          render: () => (
            <SelectApplicator
              id={`person-${index}`}
              value={getSelectedPersonValue()}
              onChange={handlePersonSelection}
              title="Create New Person"
              people={formattedPeople}
              companies={[]}
              peopleLoading={peopleLoading}
              companiesLoading={false}
              loadPeople={loadPeople}
              loadCompanies={loadCompanies}
              hideCompanies
              defaultDrawerType="People"
              disableDrawerTypeChange
              allowClear={!!applicator.personId}
            />
          )
        },
        {
          id: `applicator-company-${index}`,
          label: LayoutStrings.fields.company,
          colSpan: COL_SPAN_ONE_THIRD,
          render: () => (
            <SelectApplicator
              id={`company-${index}`}
              value={getSelectedCompanyValue()}
              onChange={handleCompanySelection}
              title="Create New Company"
              people={[]}
              companies={formattedCompanies}
              peopleLoading={false}
              companiesLoading={companiesLoading}
              loadPeople={loadPeople}
              loadCompanies={loadCompanies}
              hidePeople
              defaultDrawerType="Company"
              disableDrawerTypeChange
              allowClear={!!applicator.companyId}
            />
          )
        },
        {
          id: `remove-button-${index}`,
          label: '',
          colSpan: COL_SPAN_ONE_THIRD,
          render: () => (
            <div className="flex">
              <Button
                className="w-auto"
                type="outline"
                onClick={onRemove}
                icon={<TrashIcon color="neutral-60" />}
                dataTestId={`remove-applicator-${index}`}
              >
                {LayoutStrings.buttons.removeApplicator}
              </Button>
            </div>
          )
        }
      ]
    }
  ];

  return (
    <FormGrid showErrorLineConditionally rows={formRows} excludeFields={[]} />
  );
};

export default ApplicatorRow;
